import React, { useState, useRef, useEffect } from 'react';
import { PropTypes } from 'prop-types';
import _ from 'lodash';
import Button from '../../../componentsV2/Button/Button';
import TextInput from '../../../componentsV2/FormInputs/TextInput/TextInput';
import PasswordInput from '../../../componentsV2/FormInputs/PasswordInput/PasswordInput';
import { updateUserInfo } from '../../../utils/api/users';
import { redirectToAccount } from '../../../utils/navigation';
import { useToasts, useUnsavedChangesWarning, useCloseDialogWithUnsavedChanges } from '../../../hooks';
import { handleToastMessages, handleErrorMessages } from '../../../utils/account';
import PagesHeader from '../../../componentsV2/Headers/PagesHeader';

const AccountForm = ({
  initialData = {
    firstName: '',
    lastName: '',
    email: '',
  },
}) => {
  const [userData, setUserData] = useState({
    name_first: initialData.firstName,
    name_last: initialData.lastName,
    email: initialData.email,
    current_password: '',
    password: '',
    password_confirmation: '',
  });
  const userDataRef = useRef(userData);
  const [errorMessage, setErrorMessage] = useState({
    name_first: '',
    name_last: '',
    email: '',
    current_password: '',
    password: '',
  });
  const [isSaving, setIsSaving] = useState(false);
  const [isUnsavedChanges, setIsUnsavedChanges] = useState(false);
  const { showToast } = useToasts();

  const hasUnsavedChanges = !_.isEqual(_.omit(userData, 'current_password'), _.omit(userDataRef.current, 'current_password'));

  useEffect(() => {
    setIsUnsavedChanges(hasUnsavedChanges);
  }, [hasUnsavedChanges, userData]); // userData - track changes to userData for unsaved changes if isUnsavedChanges was set to false on previous changes

  // Browser warning for unsaved changes
  useUnsavedChangesWarning(isUnsavedChanges, () => { redirectToAccount(); });

  const handleCloseDialog = useCloseDialogWithUnsavedChanges({
    isUnsavedChanges: hasUnsavedChanges,
    setIsUnsavedChanges,
    redirectCallback: redirectToAccount,
  });

  const handleUpdateUserInfo = async () => {
    // Frontend validation for empty input fields and password confirmation
    const errors = handleErrorMessages(userData);
    if (Object.keys(errors).length > 0) {
      setErrorMessage(prev => ({ ...prev, ...errors }));
      return;
    }

    setIsSaving(true);
    try {
      // Redesign account uses v3 flow (email verification before applying email change).
      const response = await updateUserInfo(userData, 'v3');
      const data = await response.json();
      if (response.status === 200) {
        const { message, toastStatus } = handleToastMessages(initialData, userData, data);
        userDataRef.current = {
          ...userData,
          current_password: '',
        };
        showToast({ message: message || 'Changes saved.', type: toastStatus });
        setTimeout(() => {
          redirectToAccount();
        }, 1000);
      } else {
        // Inline error for incorrect current password
        if (Array.isArray(data.errors)) {
          const passwordError = data.errors.find(err => err.toLowerCase().includes('current password'));

          if (passwordError) {
            setErrorMessage(prev => ({ ...prev, current_password: 'Invalid' }));
            return;
          }
        }

        // Handle rate limiting for email changes
        if (response.status === 429) {
          throw new Error(data.error || 'Maximum email change attempts reached. Please try again in 24 hours.');
        }

        const errorText = Array.isArray(data.errors)
          ? data.errors.join(', ')
          : (data.errors || data.error || 'An error occurred while saving.');

        throw new Error(errorText);
      }
    } catch (error) {
      showToast({ message: error.message, type: 'error' });
    } finally {
      setIsSaving(false);
      setIsUnsavedChanges(false);
    }
  };

  return (
    <div className="w-[80%]">
      <div className="flex flex-col mt-2">
        <div className="flex justify-between w-full border-b border-vamos-blue-gray pb-2">
          <PagesHeader name="account_green" label="Account" size="h-9" wrapDivClass="py-0" />
          <div className="flex space-x-2">
            <Button
              label="Cancel"
              size="md"
              color="secondary"
              onClick={() => {
                handleCloseDialog();
              }}
            />
            {/* Implement save logic when backend is ready */}
            <Button
              label="Save"
              size="md"
              className="px-6"
              onClick={handleUpdateUserInfo}
              isDisabled={isSaving}
            />
          </div>
        </div>
        <div className="w-full text-left mt-8 space-y-6">
          <TextInput
            isRequired
            label="First name"
            value={userData.name_first}
            setValue={value => {
              setErrorMessage(prev => ({ ...prev, name_first: '' })); // Clear error message on change
              setUserData(prev => ({ ...prev, name_first: value }));
            }}
            className="w-full md:w-80 h-10"
            borderColor="border-vamos-blue-gray"
            asteriskWidth="w-2"
            customLabelClasses="text-lg font-medium mb-2"
            errorMessage={errorMessage.name_first || 'Include the organization’s name to save changes.'}
          />
          <TextInput
            isRequired
            label="Last name"
            value={userData.name_last}
            setValue={value => {
              setErrorMessage(prev => ({ ...prev, name_last: '' })); // Clear error message on change
              setUserData(prev => ({ ...prev, name_last: value }));
            }}
            className="w-full md:w-80 h-10"
            borderColor="border-vamos-blue-gray"
            asteriskWidth="w-2"
            customLabelClasses="text-lg font-medium mb-2"
            errorMessage={errorMessage.name_last || 'Include the organization’s short name to save changes.'}
          /><TextInput
            isRequired
            label="Email"
            dataTestId="account-email-input"
            value={userData.email}
            setValue={value => {
              setErrorMessage(prev => ({ ...prev, email: '' })); // Clear error message on change
              setUserData(prev => ({ ...prev, email: value }));
            }}
            className="w-full md:w-80 h-10"
            borderColor="border-vamos-blue-gray"
            asteriskWidth="w-2"
            customLabelClasses="text-lg font-medium mb-2"
            errorMessage={errorMessage.email}
          /><PasswordInput
            isRequired
            label="Current password"
            dataTestId="account-current-password-input"
            value={userData.current_password}
            setValue={value => {
              setErrorMessage(prev => ({ ...prev, current_password: '' })); // Clear error message on change
              setUserData(prev => ({ ...prev, current_password: value }));
            }}
            onBlur={() => {
              if (userData.current_password.trim() === '') {
                setErrorMessage(prev => ({ ...prev, current_password: 'Required' }));
              }
            }}
            className="w-full md:w-80"
            validationErrorMessage={errorMessage.current_password}
            shouldHideConfirmedIcon
            isValidationBorderEnabled
          />
          <PasswordInput
            label="New password"
            labelCustomStyle="mt-9"
            value={userData.password}
            isNewPassword
            setValue={value => {
              setErrorMessage(prev => ({ ...prev, password: '' })); // Clear error message on change
              setUserData(prev => ({ ...prev, password: value }));
            }}
            className="w-full md:w-80"
            extraInfo="Leave blank if you do not want to change password."
            validationErrorMessage={errorMessage.password}
          />
          <PasswordInput
            label="Confirm new password"
            value={userData.password_confirmation}
            isNewPassword
            setValue={value => {
              setErrorMessage(prev => ({ ...prev, password: '' })); // Clear error message on change
              setUserData(prev => ({ ...prev, password_confirmation: value }));
            }}
            className="w-full md:w-80"
            extraInfo="Leave blank if you do not want to change password."
            validationErrorMessage={errorMessage.password}
          />
        </div>
      </div>
    </div>
  );
};

AccountForm.propTypes = {
  initialData: PropTypes.shape({
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    email: PropTypes.string,
  }),
};

export default AccountForm;

